<template>
  <SoAdminPage
    title="Transactional Email Volume"
    subtitle="Monitor transactional email send volumes from be-communications, filter by template and date range"
  >
    <template #headerRight>
      <div class="d-flex align-center gap-2">
        <v-autocomplete
          v-model="selectedTemplates"
          :items="templateOptions"
          item-title="label"
          item-value="name"
          label="Template"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          variant="solo"
          flat
          hide-details
          :style="{ minWidth: '400px' }"
          data-testid="email-volumes-template-filter"
        >
          <template #prepend-item>
            <v-list-item
              data-testid="email-volumes-template-select-all"
              @click="toggleSelectAllTemplates"
            >
              <template #prepend>
                <v-checkbox-btn
                  :model-value="allTemplatesSelected"
                  :indeterminate="
                    someTemplatesSelected && !allTemplatesSelected
                  "
                />
              </template>
              <v-list-item-title>
                {{ allTemplatesSelected ? "Deselect all" : "Select all" }}
              </v-list-item-title>
            </v-list-item>
            <v-divider class="mt-2" />
          </template>
          <template #item="{ item, props: itemProps }">
            <v-list-item v-bind="itemProps" :title="undefined">
              <template #prepend>
                <v-checkbox-btn
                  :model-value="selectedTemplates.includes(item.raw.name)"
                />
              </template>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
        <SoDatePicker
          v-model="dateRange"
          type="daterange"
          label="Date Range"
          data-testid="email-volumes-date-picker"
        />
      </div>
    </template>

    <v-row class="my-4">
      <v-col cols="4">
        <v-card class="pa-4 text-center" data-testid="email-volumes-total-card">
          <div class="text-subtitle-2 text-medium-emphasis">Total Emails</div>
          <div class="text-h4 mt-1">{{ totalCountDisplay }}</div>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card
          class="pa-4 text-center"
          data-testid="email-volumes-date-range-card"
        >
          <div class="text-subtitle-2 text-medium-emphasis">Date Range</div>
          <div class="text-h6 mt-1">{{ dateRangeDisplay }}</div>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card
          class="pa-4 text-center"
          data-testid="email-volumes-template-card"
        >
          <div class="text-subtitle-2 text-medium-emphasis">
            Selected Template
          </div>
          <div class="text-h6 mt-1">
            {{
              selectedTemplates.length > 0
                ? selectedTemplates.join(", ")
                : "All"
            }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="my-4 pa-4" data-testid="email-volumes-chart">
      <SoStackedBarChart
        v-if="chartLabels.length > 0"
        :labels="chartLabels"
        :data-sets="chartDataSets"
        :selected-index="selectedIndex"
        @click:bar="onBarClick"
      />
      <div v-else class="text-center text-medium-emphasis py-8">
        {{ isChartLoading ? "Loading chart..." : "No data for chart" }}
      </div>
    </v-card>

    <SoAlert
      v-if="selectedHourMs !== null"
      type="warning"
      title="Hour filter active"
      :show-close-button="true"
      class="my-2"
      data-testid="email-volumes-hour-filter-banner"
      @close="clearHourFilter"
    >
      Filtered to: {{ selectedHourDisplay }} —
      {{ hourlyCount.toLocaleString() }} emails
    </SoAlert>

    <div
      v-if="selectedTemplates.length > 0"
      class="d-flex align-center justify-space-between mb-2"
    >
      <SoButton
        type="text"
        prepend-icon="mdi-arrow-left"
        title="All templates"
        data-testid="email-volumes-back-btn"
        @click="selectedTemplates = []"
      />
      <SoButton
        v-if="selectedTemplates.length === 1"
        type="secondary"
        prepend-icon="mdi-speedometer-slow"
        title="Add Throttle"
        :disabled="!selectedCommunicationId"
        data-testid="email-volumes-add-throttle-btn"
        @click="goToThrottleConfig"
      />
    </div>

    <SoDataTable
      v-if="selectedTemplates.length !== 1"
      :headers="summaryTableHeaders"
      :items="templateSummary"
      :loading="isChartLoading"
      :sort-by="[{ key: 'count', order: 'desc' }]"
      :default-items-per-page="50"
      :row-clickable="true"
      :searchable="false"
      data-testid="email-volumes-summary-table"
      @click:row="(item) => (selectedTemplates = [item.name])"
    >
      <template #item.name="{ internalItem }">
        <SoLink
          v-if="catalogueUrl(internalItem.raw.name)"
          :text="internalItem.raw.name"
          :href="catalogueUrl(internalItem.raw.name) ?? undefined"
          icon="external-link sm"
          data-testid="email-template-catalogue-link"
          @click.stop
        />
        <span v-else>{{ internalItem.raw.name }}</span>
      </template>
    </SoDataTable>

    <SoDataTable
      v-else
      :headers="tableHeaders"
      :items="emails"
      :loading="isListLoading"
      :sort-by="[{ key: 'processedAtDisplay', order: 'desc' }]"
      :default-items-per-page="TABLE_PAGE_SIZE"
      :searchable="false"
      data-testid="email-volumes-table"
    >
      <template #item.email="{ internalItem }">
        <SoLink
          v-if="internalItem.raw.recipient?.email"
          :text="internalItem.raw.recipient.email"
          icon="search sm"
          data-testid="email-volumes-search-by-email"
          @click="searchByEmail(internalItem.raw.recipient.email)"
        />
        <span v-else>—</span>
      </template>
      <template #item.name="{ internalItem }">
        {{ internalItem.raw.recipient?.name || "—" }}
      </template>
    </SoDataTable>
  </SoAdminPage>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs, { Dayjs } from "dayjs"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoStackedBarChart from "@/components/charts/SoStackedBarChart.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoLink from "@/components/SoLink.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import type { SoDataTableItem } from "@/types/components/SoDataTable"
  import type { DataSet } from "@/components/charts/SoLineChart.vue"
  import { EmailVolumesService } from "@/services/admin/EmailVolumesService"
  import { EmailThrottleService } from "@/services/admin/EmailThrottleService"
  import { ICommunication } from "@/models/throttleConfig/throttleConfig.types"
  import { RouteNames } from "@/types/router"
  import { useCustomerSearchStore } from "@/stores/customerSearch"
  import { QueryResult } from "@/api/utils/types"
  import type { EmailChartPoint } from "@/models/admin/emailVolume/emailVolume.parser"
  import type { EmailListResult } from "@/services/admin/EmailVolumesService"
  import { bucketEmailsByTemplate } from "./helpers/chartHelper"
  import { emailTemplateCatalogueUrl } from "@/models/admin/emailVolume/emailTemplateCatalogueMap"
  import {
    parseTemplateQueryParam,
    serializeTemplateQueryParam,
  } from "./helpers/templateQueryParam"
  import type { IEmailTemplate } from "@/models/admin/emailVolume/emailVolume.types"

  const TABLE_PAGE_SIZE = 50

  const tableHeaders = [
    { key: "email", title: "Email" },
    { key: "name", title: "Name" },
    { key: "processedAtDisplay", title: "Sent At" },
    { key: "status", title: "Status" },
  ]

  export default defineComponent({
    components: {
      SoAdminPage,
      SoStackedBarChart,
      SoDatePicker,
      SoAlert,
      SoLink,
      SoDataTable,
      SoButton,
    },
    data() {
      return {
        TABLE_PAGE_SIZE,
        tableHeaders,
        selectedTemplates: [] as string[],
        templatesQuery: null as QueryResult<IEmailTemplate[]> | null,
        selectedHourMs: null as number | null,
        dateRange: [dayjs().subtract(24, "hour").toDate(), dayjs().toDate()] as
          Date[] | null,
        listQuery: null as QueryResult<EmailListResult> | null,
        totalCountQuery: null as QueryResult<number> | null,
        chartDataQuery: null as QueryResult<EmailChartPoint[]> | null,
        communicationsQuery: null as QueryResult<ICommunication[]> | null,
      }
    },
    computed: {
      fromIso(): string | undefined {
        if (!this.dateRange || this.dateRange.length < 2) return undefined
        return dayjs(this.dateRange[0]).toISOString()
      },
      toIso(): string | undefined {
        if (!this.dateRange || this.dateRange.length < 2) return undefined
        return dayjs(this.dateRange[1]).endOf("day").toISOString()
      },
      fromDayjs(): Dayjs {
        return this.dateRange?.[0]
          ? dayjs(this.dateRange[0])
          : dayjs().subtract(24, "hour")
      },
      toDayjs(): Dayjs {
        return this.dateRange?.[1]
          ? dayjs(this.dateRange[1]).endOf("day")
          : dayjs()
      },
      dateRangeDisplay(): string {
        if (!this.dateRange || this.dateRange.length < 2) return "Not selected"
        return `${dayjs(this.dateRange[0]).format("DD MMM YYYY")} — ${dayjs(
          this.dateRange[1],
        ).format("DD MMM YYYY")}`
      },
      filters() {
        return {
          from: this.fromIso,
          to: this.toIso,
          communicationName: this.selectedTemplates,
        }
      },
      emails(): SoDataTableItem[] {
        return (this.listQuery?.data?.records ??
          []) as unknown as SoDataTableItem[]
      },
      totalCountDisplay(): string {
        return (this.totalCountQuery?.data ?? 0).toLocaleString()
      },
      chartPoints(): EmailChartPoint[] {
        return this.chartDataQuery?.data ?? []
      },
      isListLoading(): boolean {
        return Boolean(this.listQuery?.isFetching)
      },
      isChartLoading(): boolean {
        return Boolean(this.chartDataQuery?.isFetching)
      },
      chartBuckets(): { labels: Dayjs[]; dataSets: DataSet[] } {
        return bucketEmailsByTemplate(
          this.chartPoints,
          this.fromDayjs,
          this.toDayjs,
        )
      },
      chartLabels(): Dayjs[] {
        return this.chartBuckets.labels
      },
      chartDataSets(): DataSet[] {
        return this.chartBuckets.dataSets
      },
      selectedIndex(): number | null {
        if (this.selectedHourMs === null) return null
        const idx = this.chartLabels.findIndex(
          (l) => l.startOf("hour").valueOf() === this.selectedHourMs,
        )
        return idx === -1 ? null : idx
      },
      filteredChartPoints(): EmailChartPoint[] {
        const hourMs = this.selectedHourMs
        if (hourMs === null) return this.chartPoints
        return this.chartPoints.filter(
          (p) => dayjs(p.processedAt).startOf("hour").valueOf() === hourMs,
        )
      },
      templateSummary(): { name: string; count: number }[] {
        const counts = new Map<string, number>()
        for (const point of this.filteredChartPoints) {
          const name = point.communicationName ?? "unknown"
          counts.set(name, (counts.get(name) ?? 0) + 1)
        }
        return [...counts.entries()]
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => ({ name, count }))
      },
      hourlyCount(): number {
        if (this.selectedHourMs === null) return 0
        return this.templateSummary.reduce((sum, { count }) => sum + count, 0)
      },
      summaryTableHeaders(): Array<{ key: string; title: string }> {
        return [
          { key: "name", title: "Template" },
          {
            key: "count",
            title: this.selectedHourMs !== null ? "Count (this hour)" : "Count",
          },
        ]
      },
      selectedHourDisplay(): string {
        if (this.selectedHourMs === null) return ""
        return dayjs(this.selectedHourMs).format("MMM D, h:00 A")
      },
      templateOptions(): { name: string; label: string }[] {
        return (this.templatesQuery?.data ?? []).map(({ name }) => ({
          name,
          label: name,
        }))
      },
      allTemplatesSelected(): boolean {
        return (
          this.templateOptions.length > 0 &&
          this.selectedTemplates.length === this.templateOptions.length
        )
      },
      someTemplatesSelected(): boolean {
        return this.selectedTemplates.length > 0
      },
      selectedCommunicationId(): number | null {
        if (this.selectedTemplates.length !== 1) return null
        const match = (this.communicationsQuery?.data ?? []).find(
          (c) => c.name === this.selectedTemplates[0],
        )
        return match?.id ?? null
      },
    },
    watch: {
      selectedTemplates() {
        this.selectedHourMs = null
        this.syncQueryParams()
        this.runQueries()
      },
      dateRange() {
        // Clear the selected hour only if it falls outside the new date range.
        // This avoids resetting a simultaneously-set selectedHourMs when both are
        // updated in the same tick (e.g. programmatic setData or URL hydration).
        if (this.selectedHourMs !== null) {
          if (
            this.selectedHourMs < this.fromDayjs.valueOf() ||
            this.selectedHourMs > this.toDayjs.valueOf()
          ) {
            this.selectedHourMs = null
          }
        }
        this.syncQueryParams()
        this.runQueries()
      },
    },
    created() {
      const { template, from, to } = this.$route.query
      this.selectedTemplates = parseTemplateQueryParam(
        template as string | string[] | undefined,
      )
      if (typeof from === "string" && typeof to === "string") {
        this.dateRange = [dayjs(from).toDate(), dayjs(to).toDate()]
      }
    },
    mounted() {
      this.templatesQuery = EmailVolumesService.getTemplates()
      this.communicationsQuery = EmailThrottleService.getCommunications()
      this.runQueries()
    },
    methods: {
      catalogueUrl(name: string): string | null {
        return emailTemplateCatalogueUrl(name)
      },
      syncQueryParams() {
        this.$router.replace({
          query: {
            ...(serializeTemplateQueryParam(this.selectedTemplates) && {
              template: serializeTemplateQueryParam(this.selectedTemplates),
            }),
            ...(this.fromIso && { from: this.fromIso }),
            ...(this.toIso && { to: this.toIso }),
          },
        })
      },
      toggleSelectAllTemplates() {
        this.selectedTemplates = this.allTemplatesSelected
          ? []
          : this.templateOptions.map((t) => t.name)
      },
      runQueries() {
        if (this.selectedTemplates.length === 1) {
          this.listQuery = EmailVolumesService.getEmailList(
            0,
            TABLE_PAGE_SIZE,
            this.filters,
          )
        }
        this.totalCountQuery = EmailVolumesService.getTotalCount(this.filters)
        this.chartDataQuery = EmailVolumesService.getChartData(this.filters)
      },
      clearHourFilter() {
        this.selectedHourMs = null
      },
      onBarClick({ index: _index, label }: { index: number; label: string }) {
        const clickedMs = dayjs(label).startOf("hour").valueOf()
        if (this.selectedHourMs !== null && this.selectedHourMs === clickedMs) {
          this.selectedHourMs = null
        } else {
          this.selectedHourMs = clickedMs
        }
      },
      goToThrottleConfig() {
        if (!this.selectedCommunicationId) return
        this.$router.push({
          name: RouteNames.AdminEmailThrottles,
          query: {
            communicationId: String(this.selectedCommunicationId),
            communicationName: this.selectedTemplates[0],
          },
        })
      },
      searchByEmail(email: string) {
        useCustomerSearchStore().search("email", email)
        this.$router.push({ name: RouteNames.CustomerSearch })
      },
    },
  })
</script>
